// Motor de la guía interactiva: resalta la zona real de cada paso (cuatro
// capas oscuras alrededor y un anillo; el hueco queda libre para tocar de
// verdad), pone la tarjeta con la explicación y pasa de pantalla tocando el
// menú como lo haría la persona. Se monta una vez en Layout; el estado vive en
// context/GuiaContext.js y el guion en components/guia/guionBienvenida.js.
// Nunca bloquea: si una zona no aparece en 6 s, el paso opcional se salta y el
// resto se enseña como texto con Siguiente; "Saltar la guía" siempre está.
// Novedades (`estado.novedad`, ver NovedadesPendientes.js): misma tarjeta con
// la cabecera "Novedad", "Entendido" en el último paso (resaltado o no) y
// "Cerrar" en vez de "Saltar la guía", sin diálogo de confirmación.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle, ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Loader2, Pointer,
} from 'lucide-react';
import { textoErrorApi } from '../../api/api';
import { useGuia } from '../../context/GuiaContext';
import { useVistaEmpleado } from '../../context/VistaEmpleadoContext';
import { MENU_ERP } from '../../utils/modulosErp';
import { DialogoConfirmar } from '../empleados/RegistroHorarioEmpleado';
import { zona } from './guionBienvenida';

const MARGEN = 6; // px alrededor de la zona resaltada
const INTERVALO = 150; // ms entre comprobaciones
const ESPERA_MAX = 6000; // ms hasta dar una zona por no encontrada
const ESPERA_CORTA = 2000; // zona opcional en la misma pantalla que la anterior ya cargada
const ESPERA_SIN_TOCAR = 10000; // ms hasta ofrecer "No me deja tocarlo" en un paso de clic
const PERDIDAS_MAX = 3; // cambios de pantalla por su cuenta antes de preguntar
const ALTO_TARJETA = 260; // px aproximados de la tarjeta en escritorio

const lista = (selector) => {
  if (Array.isArray(selector)) return selector;
  return selector ? [selector] : [];
};

const estaALaVista = (el) => {
  if (!el || !el.isConnected) return false;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return false;
  const estilo = window.getComputedStyle(el);
  return estilo.visibility !== 'hidden' && estilo.display !== 'none';
};

// Primer elemento a la vista del primer selector que tenga alguno
// (también lo usa NovedadesPendientes.js para saber si una novedad aplica)
export const buscar = (selector) => {
  for (const s of lista(selector)) {
    let nodos = [];
    try {
      nodos = document.querySelectorAll(s);
    } catch (e) {
      nodos = [];
    }
    for (const el of nodos) {
      if (estaALaVista(el)) return el;
    }
  }
  return null;
};

const rutaSinQuery = (ruta) => (ruta ? String(ruta).split('?')[0] : null);

const infoMenu = (moduleId) => {
  for (const item of MENU_ERP) {
    if (item.moduleId === moduleId && !item.children) return { label: item.label, grupo: null };
    for (const hijo of item.children || []) {
      if (hijo.moduleId === moduleId) {
        return { label: hijo.label, grupo: { moduleId: item.moduleId, label: item.label } };
      }
    }
  }
  return null;
};

const medir = (el) => {
  const r = el.getBoundingClientRect();
  return {
    top: Math.round(r.top - MARGEN),
    left: Math.round(r.left - MARGEN),
    width: Math.round(r.width + MARGEN * 2),
    height: Math.round(r.height + MARGEN * 2),
  };
};

// Botón que hay que tocar para llegar a la pantalla del paso
const objetivoMenu = (info, moduleId, v) => {
  if (v.isMobile && !v.menuAbierto) {
    return { selector: zona('menu-abrir'), titulo: 'Abre el menú', texto: `Toca el botón del menú para ir a ${info.label}.` };
  }
  if (!v.isMobile && v.menuOculto) {
    return { selector: zona('menu-mostrar'), titulo: 'Abre el menú', texto: `Toca aquí para ver el menú e ir a ${info.label}.` };
  }
  const enlace = zona(`menu-${moduleId}`);
  if (info.grupo && !buscar(enlace)) {
    return {
      selector: zona(`menu-grupo-${info.grupo.moduleId}`),
      titulo: `Abre ${info.grupo.label}`,
      texto: `${info.label} está dentro de ${info.grupo.label}. Tócalo.`,
    };
  }
  return { selector: enlace, titulo: `Ve a ${info.label}`, texto: `Toca ${info.label}.` };
};

const ladoTarjeta = (rect, posicion) => {
  if (posicion === 'arriba' || posicion === 'abajo') return posicion;
  if (!rect) return 'abajo';
  const alto = window.innerHeight;
  const arriba = rect.top;
  const abajo = alto - (rect.top + rect.height);
  return abajo >= arriba ? 'abajo' : 'arriba';
};

const claseBoton = 'h-12 inline-flex items-center justify-center gap-1 rounded-xl font-semibold';

const GuiaInteractiva = ({ isMobile = false, menuAbierto = false, menuOculto = false, cerrarMenu = null }) => {
  const {
    estado, irAPaso, actualizarGuia, pararGuia, posponerGuia, terminarGuia,
  } = useGuia();
  const { simulando } = useVistaEmpleado();
  const location = useLocation();
  const navigate = useNavigate();
  const [vista, setVista] = useState(null);
  const [confirmarSalir, setConfirmarSalir] = useState(false);
  const [terminando, setTerminando] = useState(false);
  const [error, setError] = useState(null);

  // Lo último de cada render, para el temporizador y los oyentes
  const vivo = useRef({});
  vivo.current = {
    estado, location, isMobile, menuAbierto, menuOculto, cerrarMenu, navigate, irAPaso, actualizarGuia, confirmarSalir,
  };
  const cron = useRef({ clave: null });
  const esperando = useRef(null);
  const encontrados = useRef(new Set());
  const vistaTexto = useRef(null);
  const observador = useRef(null);
  const observado = useRef(null);
  const tickRef = useRef(() => {});

  const poner = useCallback((v) => {
    const texto = JSON.stringify(v);
    if (vistaTexto.current !== texto) {
      vistaTexto.current = texto;
      setVista(v);
    }
  }, []);

  const avanzar = useCallback(() => {
    const e = vivo.current.estado;
    if (!e || e.indice >= e.pasos.length - 1) return;
    vivo.current.irAPaso(e.indice + 1, { sentido: 1 });
  }, []);

  // Salta el paso en el sentido en que se iba (Atrás salta hacia atrás)
  const saltar = useCallback(() => {
    const e = vivo.current.estado;
    if (!e) return;
    const sentido = e.sentido === -1 ? -1 : 1;
    const destino = e.indice + sentido;
    if (destino < 0) {
      vivo.current.irAPaso(e.indice + 1, { sentido: 1 });
      return;
    }
    vivo.current.irAPaso(Math.min(destino, e.pasos.length - 1), { sentido, directa: sentido < 0 });
  }, []);

  const vigilar = useCallback((el, alCambiar) => {
    if (observado.current === el) return;
    observado.current = el;
    if (observador.current) observador.current.disconnect();
    if (el && typeof ResizeObserver !== 'undefined') {
      observador.current = new ResizeObserver(alCambiar);
      observador.current.observe(el);
    }
  }, []);

  const tick = useCallback(() => {
    const v = vivo.current;
    const e = v.estado;
    if (!e || v.confirmarSalir) {
      poner(null);
      return;
    }
    const paso = e.pasos[e.indice];
    if (!paso) {
      poner(null);
      return;
    }
    const ahora = Date.now();
    const clave = `${e.indice}|${e.pasos.length}`;
    if (cron.current.clave !== clave) {
      cron.current = {
        clave, faltaDesde: null, buscandoDesde: null, navegadoEn: null, cerrado: false, scrollDe: null, hecho: false, clicDesde: null,
      };
      esperando.current = null;
    }
    const c = cron.current;
    if (c.hecho) return;
    const base = {
      indice: e.indice, total: e.pasos.length, titulo: paso.titulo, texto: paso.texto, final: !!paso.final, aviso: null,
    };
    const programar = () => setTimeout(() => tickRef.current(), 0);

    // Tras tocar la zona: esperar a que aparezca lo que abre (modal, pestaña)
    if (esperando.current) {
      if (buscar(esperando.current.selector) || ahora - esperando.current.desde > ESPERA_MAX) {
        esperando.current = null;
        c.hecho = true;
        avanzar();
        return;
      }
      poner({ ...base, modo: 'esperando' });
      return;
    }

    if (paso.requiere && (e.fallidos || []).includes(paso.requiere)) {
      c.hecho = true;
      saltar();
      return;
    }

    const rutaPaso = rutaSinQuery(paso.ruta);
    const { pathname, search } = v.location;
    const enRuta = !rutaPaso || pathname === rutaPaso;

    // Resalta un botón que hay que tocar para seguir (menú, enlace)
    const resaltarNavegacion = (obj, el) => {
      if (c.clicDesde === null) c.clicDesde = ahora;
      if (c.scrollDe !== el) {
        c.scrollDe = el;
        try { el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' }); } catch (err) { /* sin scroll */ }
      }
      vigilar(el, programar);
      poner({
        ...base,
        modo: 'resaltar',
        tipo: 'clic',
        nav: true,
        titulo: obj.titulo,
        texto: obj.texto,
        rect: medir(el),
        posicion: 'auto',
        selector: obj.selector,
        sinTocar: ahora - c.clicDesde > ESPERA_SIN_TOCAR,
      });
    };

    const navegarDirecto = (reemplazar = false) => {
      if (c.navegadoEn !== null) return;
      c.navegadoEn = ahora;
      v.navigate(paso.ruta, reemplazar ? { replace: true } : undefined);
    };

    if (!enRuta) {
      // Se fue por su cuenta de la pantalla del paso: la guía espera
      if (e.rutaOk) {
        poner({ ...base, modo: (e.perdidas || 0) >= PERDIDAS_MAX ? 'preguntar' : 'pausa' });
        return;
      }
      if (c.navegadoEn !== null && ahora - c.navegadoEn > ESPERA_MAX) {
        poner({ ...base, modo: 'texto', aviso: 'No encuentro esta pantalla ahora mismo.' });
        return;
      }
      const info = paso.menu ? infoMenu(paso.menu) : null;
      if (e.directa || !info) {
        navegarDirecto();
        poner({ ...base, modo: 'buscando' });
        return;
      }
      const obj = objetivoMenu(info, paso.menu, v);
      const el = buscar(obj.selector);
      if (!el) {
        if (c.faltaDesde === null) c.faltaDesde = ahora;
        if (ahora - c.faltaDesde > ESPERA_MAX) navegarDirecto();
        poner({ ...base, modo: 'buscando' });
        return;
      }
      c.faltaDesde = null;
      resaltarNavegacion(obj, el);
      return;
    }

    // En la pantalla del paso
    if (e.directa) {
      if (paso.ruta && `${pathname}${search}` !== paso.ruta && c.navegadoEn === null) {
        navegarDirecto(true);
        poner({ ...base, modo: 'buscando' });
        return;
      }
      v.actualizarGuia({ directa: false, rutaOk: true });
    } else if (rutaPaso && !e.rutaOk) {
      v.actualizarGuia({ rutaOk: true });
    }

    if (paso.enMenu) {
      let obj = null;
      if (v.isMobile && !v.menuAbierto) obj = { selector: zona('menu-abrir'), titulo: 'Abre el menú', texto: 'Toca el botón del menú.' };
      else if (!v.isMobile && v.menuOculto) obj = { selector: zona('menu-mostrar'), titulo: 'Abre el menú', texto: 'Toca aquí para ver el menú.' };
      if (obj) {
        const el = buscar(obj.selector);
        if (el) {
          resaltarNavegacion(obj, el);
          return;
        }
      }
    } else if (v.isMobile && v.menuAbierto && !c.cerrado && v.cerrarMenu) {
      c.cerrado = true;
      v.cerrarMenu();
    }

    if (!paso.selector) {
      vigilar(null, programar);
      poner({ ...base, modo: 'texto' });
      return;
    }

    const el = buscar(paso.selector);
    if (el) {
      encontrados.current.add(e.indice);
      if ((e.fallidos || []).includes(paso.clave)) {
        v.actualizarGuia((x) => ({ fallidos: (x.fallidos || []).filter((k) => k !== paso.clave) }));
      }
      if (c.scrollDe !== el) {
        c.scrollDe = el;
        try { el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' }); } catch (err) { /* sin scroll */ }
      }
      vigilar(el, programar);
      const esClic = paso.accion === 'clic';
      if (esClic && c.clicDesde === null) c.clicDesde = ahora;
      poner({
        ...base,
        modo: 'resaltar',
        tipo: esClic ? 'clic' : 'observar',
        nav: false,
        rect: medir(el),
        posicion: paso.posicion || 'auto',
        selector: paso.selector,
        sinTocar: esClic && ahora - c.clicDesde > ESPERA_SIN_TOCAR,
      });
      return;
    }

    vigilar(null, programar);
    if (c.buscandoDesde === null) c.buscandoDesde = ahora;
    const anterior = e.pasos[e.indice - 1];
    const corto = paso.opcional && anterior && encontrados.current.has(e.indice - 1)
      && rutaSinQuery(anterior.ruta) === rutaPaso;
    if (ahora - c.buscandoDesde < (corto ? ESPERA_CORTA : ESPERA_MAX)) {
      poner({ ...base, modo: 'buscando' });
      return;
    }
    if (!(e.fallidos || []).includes(paso.clave)) {
      v.actualizarGuia((x) => ({ fallidos: [...(x.fallidos || []), paso.clave] }));
    }
    if (paso.opcional && !paso.textoSiFalta) {
      c.hecho = true;
      saltar();
      return;
    }
    poner({
      ...base,
      modo: 'texto',
      texto: paso.textoSiFalta || paso.texto,
      aviso: paso.textoSiFalta ? null : 'No encuentro esta zona ahora mismo.',
    });
  }, [avanzar, poner, saltar, vigilar]);

  tickRef.current = tick;

  const activa = !!estado;

  // Comprobaciones periódicas, al cambiar el DOM, al hacer scroll y al girar
  useEffect(() => {
    if (!activa) {
      poner(null);
      return undefined;
    }
    tick();
    const intervalo = setInterval(tick, INTERVALO);
    let marco = null;
    const programar = () => {
      if (marco !== null) return;
      marco = window.requestAnimationFrame(() => {
        marco = null;
        tick();
      });
    };
    const mutaciones = typeof MutationObserver !== 'undefined' ? new MutationObserver(programar) : null;
    if (mutaciones) mutaciones.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', programar, true);
    window.addEventListener('resize', programar);
    return () => {
      clearInterval(intervalo);
      if (marco !== null) window.cancelAnimationFrame(marco);
      if (mutaciones) mutaciones.disconnect();
      window.removeEventListener('scroll', programar, true);
      window.removeEventListener('resize', programar);
      if (observador.current) observador.current.disconnect();
      observador.current = null;
      observado.current = null;
    };
  }, [activa, tick, poner]);

  // Reacción inmediata a los cambios de paso, pantalla o menú
  useEffect(() => {
    if (activa) tick();
  }, [activa, estado, location.pathname, location.search, isMobile, menuAbierto, menuOculto, confirmarSalir, tick]);

  // Cambios de pantalla por su cuenta lejos del paso: al tercero se pregunta
  useEffect(() => {
    const e = vivo.current.estado;
    if (!e || e.ultimaRuta === location.pathname) return;
    const rutaPaso = rutaSinQuery(e.pasos[e.indice]?.ruta);
    const fuera = !!e.rutaOk && !!rutaPaso && location.pathname !== rutaPaso;
    actualizarGuia((x) => ({ ultimaRuta: location.pathname, perdidas: fuera ? (x.perdidas || 0) + 1 : (x.perdidas || 0) }));
  }, [location.pathname, actualizarGuia]);

  // La vista previa se cierra al salir de la simulación
  useEffect(() => {
    if (estado?.previsualizar && !simulando) pararGuia();
  }, [estado?.previsualizar, simulando, pararGuia]);

  // Pasos de clic: avanza cuando la persona toca la zona de verdad
  const claveClic = vista && vista.modo === 'resaltar' && vista.tipo === 'clic'
    ? JSON.stringify([vista.selector, !!vista.nav, vista.indice])
    : null;
  useEffect(() => {
    if (!claveClic) return undefined;
    const [selector, nav, indice] = JSON.parse(claveClic);
    const alClic = (ev) => {
      const el = buscar(selector);
      if (!el || !el.contains(ev.target)) return;
      if (nav) {
        setTimeout(() => tickRef.current(), 60);
        return;
      }
      const e = vivo.current.estado;
      const c = cron.current;
      if (!e || e.indice !== indice || c.hecho) return;
      const paso = e.pasos[e.indice];
      if (paso && paso.esperar) {
        esperando.current = { selector: paso.esperar, desde: Date.now() };
        setTimeout(() => tickRef.current(), 60);
      } else {
        c.hecho = true;
        setTimeout(avanzar, 250);
      }
    };
    document.addEventListener('click', alClic, true);
    return () => document.removeEventListener('click', alClic, true);
  }, [claveClic, avanzar]);

  if (!estado) return null;

  const paso = estado.pasos[estado.indice] || {};
  const previsualizar = !!estado.previsualizar;
  const novedad = !!estado.novedad;

  if (confirmarSalir) {
    return (
      <DialogoConfirmar
        abierto
        titulo={previsualizar ? '¿Cerrar la vista previa de la guía?' : '¿Dejar la guía para luego?'}
        descripcion={previsualizar ? 'No se guarda nada.' : 'Puedes retomarla desde Mi Inicio.'}
        textoConfirmar={previsualizar ? 'Cerrar' : 'Salir de la guía'}
        textoCancelar="Seguir con la guía"
        onConfirmar={() => {
          setConfirmarSalir(false);
          if (previsualizar) pararGuia();
          else posponerGuia();
        }}
        onCancelar={() => setConfirmarSalir(false)}
        testId="guia-confirmar-salir"
      />
    );
  }

  if (!vista) return null;

  const siguiente = () => {
    esperando.current = null;
    cron.current.hecho = true;
    avanzar();
  };

  const atras = () => {
    if (estado.indice <= 0) return;
    esperando.current = null;
    cron.current.hecho = true;
    irAPaso(estado.indice - 1, { directa: true, sentido: -1 });
  };

  const terminar = async () => {
    setTerminando(true);
    setError(null);
    try {
      await terminarGuia();
    } catch (e) {
      console.error('Error marcando la guía como vista:', e);
      setError(textoErrorApi(e?.response?.data?.detail, 'No se ha podido guardar. Inténtalo otra vez.'));
    } finally {
      setTerminando(false);
    }
  };

  // "No me deja tocarlo": sigue sin el clic (nunca se queda atascada)
  const seguirSinTocar = () => {
    if (vista.nav) {
      cron.current.navegadoEn = Date.now();
      navigate(paso.ruta || '/dashboard-empleado');
      return;
    }
    siguiente();
  };

  const volverAlPaso = () => {
    actualizarGuia({ perdidas: 0 });
    if (paso.ruta) navigate(paso.ruta);
  };

  // Guía en pausa mientras la persona está en otra pantalla
  if (vista.modo === 'pausa') {
    return (
      <div
        className="fixed z-[96] bottom-3 inset-x-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-[340px] bg-white border border-gray-200 shadow-lg rounded-xl px-3 py-2 flex items-center justify-between gap-3"
        role="status"
        data-testid="guia-pausa"
      >
        <span className="text-sm text-gray-700">La guía está en pausa</span>
        <button
          type="button"
          onClick={volverAlPaso}
          className="h-10 px-3 rounded-lg bg-duetto-primary text-white text-sm font-semibold shrink-0"
          data-testid="guia-volver"
        >
          Volver a la guía
        </button>
      </div>
    );
  }

  if (vista.modo === 'preguntar') {
    return (
      <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:w-[360px] z-[96] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-200 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]" role="dialog" aria-label="Guía" data-testid="guia-preguntar">
        <p className="text-lg font-semibold text-gray-900">¿Seguimos la guía?</p>
        <p className="mt-1 text-base text-gray-700">Te llevo a la pantalla en la que estábamos.</p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => (previsualizar ? pararGuia() : posponerGuia())}
            className={`${claseBoton} flex-1 border border-gray-300 bg-white text-gray-700`}
            data-testid="guia-preguntar-no"
          >
            No
          </button>
          <button
            type="button"
            onClick={volverAlPaso}
            className={`${claseBoton} flex-1 bg-duetto-primary text-white`}
            data-testid="guia-preguntar-si"
          >
            Sí
          </button>
        </div>
      </div>
    );
  }

  const rect = vista.modo === 'resaltar' ? vista.rect : null;
  const esClic = vista.modo === 'resaltar' && vista.tipo === 'clic';
  const lado = ladoTarjeta(rect, vista.posicion);
  const progreso = Math.round(((vista.indice + 1) / vista.total) * 100);
  const alto = window.innerHeight;
  const ancho = window.innerWidth;

  // Capas oscuras alrededor del hueco
  let capas = null;
  if (rect) {
    const arriba = Math.max(0, Math.min(alto, rect.top));
    const abajo = Math.max(arriba, Math.min(alto, rect.top + rect.height));
    const izquierda = Math.max(0, Math.min(ancho, rect.left));
    const derecha = Math.max(izquierda, Math.min(ancho, rect.left + rect.width));
    const claseCapa = 'fixed z-[95] bg-black/60';
    const bloquea = { pointerEvents: 'auto' };
    capas = (
      <>
        <div className={claseCapa} style={{ ...bloquea, top: 0, left: 0, right: 0, height: arriba }} aria-hidden="true" />
        <div className={claseCapa} style={{ ...bloquea, top: abajo, left: 0, right: 0, bottom: 0 }} aria-hidden="true" />
        <div className={claseCapa} style={{ ...bloquea, top: arriba, left: 0, width: izquierda, height: abajo - arriba }} aria-hidden="true" />
        <div className={claseCapa} style={{ ...bloquea, top: arriba, left: derecha, right: 0, height: abajo - arriba }} aria-hidden="true" />
        {/* En "observar" la zona se ve pero no se toca (nada se ficha sin querer) */}
        {!esClic && (
          <div className="fixed z-[95]" style={{ ...bloquea, top: arriba, left: izquierda, width: derecha - izquierda, height: abajo - arriba }} aria-hidden="true" />
        )}
        <div
          className={`fixed z-[95] rounded-xl ring-4 ring-amber-400 pointer-events-none ${esClic ? 'animate-pulse' : ''}`}
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          aria-hidden="true"
          data-testid="guia-resalte"
        />
      </>
    );
  } else if (vista.modo !== 'esperando') {
    capas = (
      <div
        className={`fixed inset-0 z-[95] ${vista.modo === 'buscando' ? 'bg-black/30' : 'bg-black/60'}`}
        style={{ pointerEvents: 'auto' }}
        aria-hidden="true"
      />
    );
  }

  // "Toca aquí" junto a la zona, en el lado contrario a la tarjeta
  let burbuja = null;
  if (esClic && rect) {
    const encima = lado === 'abajo' ? rect.top > 56 : rect.top + rect.height > alto - 56;
    const centro = Math.max(70, Math.min(ancho - 70, rect.left + rect.width / 2));
    burbuja = (
      <div
        className="fixed z-[96] -translate-x-1/2 pointer-events-none flex items-center gap-1 rounded-full bg-amber-400 text-amber-950 text-sm font-semibold px-3 py-1.5 shadow-lg animate-bounce"
        style={encima ? { left: centro, top: rect.top - 42 } : { left: centro, top: rect.top + rect.height + 8 }}
        aria-hidden="true"
      >
        {encima ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
        Toca aquí
      </div>
    );
  }

  // Tarjeta: en móvil, hoja a todo el ancho (abajo o arriba); en escritorio, junto a la zona
  let claseTarjeta;
  let estiloTarjeta;
  if (isMobile) {
    claseTarjeta = lado === 'arriba'
      ? 'fixed inset-x-0 top-0 rounded-b-2xl pt-[max(1rem,env(safe-area-inset-top))] pb-4'
      : 'fixed inset-x-0 bottom-0 rounded-t-2xl pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]';
  } else if (!rect) {
    claseTarjeta = 'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] rounded-2xl py-4';
  } else {
    const izquierda = Math.max(16, Math.min(ancho - 376, rect.left));
    const debajo = alto - (rect.top + rect.height);
    claseTarjeta = 'fixed w-[360px] rounded-2xl py-4';
    if (debajo >= ALTO_TARJETA + 12) estiloTarjeta = { left: izquierda, top: rect.top + rect.height + 12 };
    else if (rect.top >= ALTO_TARJETA + 12) estiloTarjeta = { left: izquierda, bottom: alto - rect.top + 12 };
    else estiloTarjeta = { right: 16, bottom: 16 };
  }

  // En una novedad el último paso se cierra con "Entendido" aunque esté resaltado
  const ultimoNovedad = novedad && vista.indice >= vista.total - 1;
  const mostrarSiguiente = !esClic && vista.modo !== 'esperando' && !vista.final && !ultimoNovedad;
  const mostrarTerminar = novedad ? ultimoNovedad : (vista.final && vista.modo === 'texto');
  let textoTerminar = 'Terminar';
  if (novedad) textoTerminar = 'Entendido';
  else if (terminando) textoTerminar = 'Guardando...';
  let textoSaltar = previsualizar ? 'Cerrar la vista previa' : 'Saltar la guía';
  if (novedad) textoSaltar = 'Cerrar';

  return (
    <>
      {capas}
      {burbuja}
      <div
        className={`${claseTarjeta} z-[96] bg-white shadow-2xl border border-gray-200 px-5`}
        style={estiloTarjeta}
        role="dialog"
        aria-modal="false"
        aria-labelledby="guia-titulo"
        data-testid="guia-tarjeta"
      >
        <div className="flex items-center justify-between gap-2">
          {novedad ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-duetto-primary" data-testid="guia-novedad">
              Novedad
            </p>
          ) : (
            <p className="text-xs font-medium text-gray-500" data-testid="guia-contador">
              Paso {vista.indice + 1} de {vista.total}
            </p>
          )}
          {novedad && vista.total > 1 && (
            <p className="text-xs font-medium text-gray-500" data-testid="guia-contador">
              {vista.indice + 1} de {vista.total}
            </p>
          )}
          {previsualizar && (
            <p className="text-xs font-medium text-orange-700 truncate">Vista previa · {estado.nombre || 'empleado'}</p>
          )}
        </div>
        <div
          className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={vista.total}
          aria-valuenow={vista.indice + 1}
        >
          <div className="h-full bg-duetto-primary transition-all" style={{ width: `${progreso}%` }} />
        </div>

        <h2 id="guia-titulo" className="mt-3 text-lg font-semibold text-gray-900">{vista.titulo}</h2>
        <p className="mt-1 text-base text-gray-700 leading-relaxed" aria-live="polite">{vista.texto}</p>

        {vista.aviso && (
          <p className="mt-2 text-sm text-amber-800 flex items-start gap-1.5" data-testid="guia-aviso">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {vista.aviso}
          </p>
        )}
        {(vista.modo === 'buscando' || vista.modo === 'esperando') && (
          <p className="mt-2 text-sm text-gray-500 flex items-center gap-1.5">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" /> Un momento...
          </p>
        )}
        {esClic && (
          <p className="mt-2 text-sm font-semibold text-amber-800 flex items-center gap-1.5" data-testid="guia-toca">
            <Pointer className="w-4 h-4 shrink-0" /> Toca la zona marcada para seguir
          </p>
        )}
        {error && (
          <p className="mt-2 text-sm text-red-700" role="alert" data-testid="guia-error">{error}</p>
        )}

        <div className="mt-4 flex gap-2">
          {vista.indice > 0 && (
            <button
              type="button"
              onClick={atras}
              disabled={terminando}
              aria-label="Atrás"
              className={`${claseBoton} w-12 sm:w-auto sm:px-4 shrink-0 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50`}
              data-testid="guia-atras"
            >
              <ChevronLeft className="w-5 h-5" /> <span className="hidden sm:inline font-medium">Atrás</span>
            </button>
          )}
          {mostrarSiguiente && (
            <button
              type="button"
              onClick={siguiente}
              className={`${claseBoton} flex-1 px-5 bg-duetto-primary text-white hover:bg-opacity-90`}
              data-testid="guia-siguiente"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {mostrarTerminar && (
            <button
              type="button"
              onClick={terminar}
              disabled={terminando}
              className={`${claseBoton} flex-1 px-5 bg-duetto-primary text-white hover:bg-opacity-90 disabled:opacity-60`}
              data-testid="guia-terminar"
            >
              {textoTerminar}
            </button>
          )}
          {esClic && vista.sinTocar && (
            <button
              type="button"
              onClick={seguirSinTocar}
              className={`${claseBoton} flex-1 px-3 border border-gray-300 bg-white text-gray-700 font-medium`}
              data-testid="guia-sin-tocar"
            >
              No me deja tocarlo
            </button>
          )}
        </div>
        {!mostrarTerminar && (
          <button
            type="button"
            onClick={() => (novedad ? posponerGuia() : setConfirmarSalir(true))}
            className="mt-2 h-11 w-full text-sm text-gray-500 underline-offset-2 hover:underline"
            data-testid="guia-saltar"
          >
            {textoSaltar}
          </button>
        )}
      </div>
    </>
  );
};

export default GuiaInteractiva;
